/**
 * Water Mitigation Equipment Calculator (IICRC S500) — public calculator page
 * (PRP-CALC-1 S3). Sizes air movers and dehumidification from room dimensions,
 * water class, and category, and produces an S500-referenced narrative.
 * Carries the /tools SEO treatment: per-route meta + JSON-LD, method and
 * classes/categories content sections, app promo band.
 */
import { useState } from 'react';

import { Button } from '../../components/ui/Button';
import ToolPageShell, { DimensionDivider, Eyebrow } from './ToolPageShell';
import AppPromo from './AppPromo';
import CalcResults from './CalcResults';
import {
  MethodSection,
  FaqSection,
  SourcesSection,
  NumberField,
  SelectField,
  type FaqItem,
} from './_shared';
import { useSeo, faqJsonLd, calculatorJsonLd } from './useSeo';
import {
  CalcAPI,
  type CalcResponse,
  type WaterClass,
  type WaterCategory,
} from './calcApi';

const FAQ_ITEMS: FaqItem[] = [
  {
    q: 'How many air movers do I need per room?',
    a: 'The S500 working baseline is one air mover for every 10 to 16 linear feet of wet wall, plus one for the room to start, plus one for each inset, offset, or closet that would otherwise sit in dead air. Higher classes and wet ceilings push the count up because more surface is evaporating at once. The tool applies that to your room dimensions and class, and you confirm it against moisture readings on site.',
  },
  {
    q: 'How do I size the dehumidifier for a water loss?',
    a: 'Dehumidification is sized from the volume of the wet space and the water class, then divided by the pints-per-day rating of the units you plan to run. A conventional refrigerant dehu covers less volume per machine than a low grain refrigerant (LGR) unit, so an LGR usually means fewer machines on the same room. The tool returns a pint capacity and a unit count, and names the Xactimate WTR band it lands in.',
  },
  {
    q: 'What do water damage classes 1 through 4 mean?',
    a: 'Class describes the evaporation load. Class 1 is a small part of a room with minimal wet material. Class 2 is a whole room with water wicked up under 24 inches. Class 3 is water that came from overhead, so walls and ceiling are saturated. Class 4 is bound water held in low-permeance materials like hardwood, plaster, and concrete that need specialty drying. The higher the class, the more equipment and the longer the dry-out.',
  },
  {
    q: 'What is the difference between category 1, 2, and 3 water?',
    a: 'Category is about contamination, not quantity. Category 1 is clean water from a supply line. Category 2 is gray water from an appliance discharge or overflow. Category 3 is grossly contaminated water: sewage, rising ground water, or outside flood water. Category drives containment, antimicrobial application, and what porous materials must be removed rather than dried.',
  },
  {
    q: 'Why does category 3 water change the scope so much?',
    a: 'Category 3 water carries pathogens, so drying alone is not enough. Porous materials that soaked it up (drywall, insulation, carpet pad, and often carpet) come out, the cavity gets cleaned and treated with an antimicrobial, and containment keeps the contamination from spreading. That is a demolition and remediation scope on top of the drying setup, which is why a category 3 loss costs far more than a clean-water loss of the same size.',
  },
  {
    q: 'How many days of drying should I plan for?',
    a: 'Most structural dry-outs run 3 to 5 days. Class 1 and clean class 2 losses can finish in 2 to 3 days; class 3 and class 4 losses with saturated ceilings or bound water often run 5 days or more. The equipment stays on the loss and runs continuously until daily readings show the material has met the drying goal, so the day count is confirmed by the log, not fixed up front.',
  },
  {
    q: 'What do I bill for daily equipment monitoring?',
    a: 'Each day a technician returns to take moisture and psychrometric readings, adjust the equipment, and document progress. In Xactimate that is a monitoring or drying-check line, billed per visit, separate from the equipment itself. The readings are what justify keeping the equipment on the loss, so the monitoring line and the equipment days should agree.',
  },
  {
    q: 'Can I bill drying equipment in Xactimate?',
    a: 'Yes. The air movers and dehumidifiers are WTR equipment items in Xactimate, priced per day against your price list. You bill the unit count times the number of days each was on the loss. This tool outputs the counts and the WTR codes; Xactimate prices them regionally at import, and the drying log backs up the days.',
  },
];

export default function WaterMitigationCalculatorPage() {
  useSeo({
    title:
      'Water Mitigation Equipment Calculator — Air Movers & Dehumidifiers (IICRC S500) | Antero Trail',
    description:
      'Free air mover and dehumidifier calculator using the IICRC S500 method. Enter the room, water class, and category to size drying equipment and get a justification you can bill in Xactimate.',
    path: '/tools/water-mitigation-calculator',
    jsonLd: [
      calculatorJsonLd({
        name: 'Water Mitigation Equipment Calculator (IICRC S500)',
        description:
          'Sizes air movers and dehumidification from room dimensions, water class, and category using the IICRC S500 method, and writes an S500-referenced justification for restoration estimates.',
        path: '/tools/water-mitigation-calculator',
      }),
      faqJsonLd(FAQ_ITEMS),
    ],
  });

  const [length, setLength] = useState<number | ''>(20);
  const [width, setWidth] = useState<number | ''>(15);
  const [area, setArea] = useState<number | ''>('');
  const [ceiling, setCeiling] = useState<number>(8);
  const [waterClass, setWaterClass] = useState<WaterClass>(2);
  const [category, setCategory] = useState<WaterCategory>(1);
  const [chambers, setChambers] = useState<number>(1);
  const [insets, setInsets] = useState<number>(0);
  const [perimeter, setPerimeter] = useState<number | ''>('');

  const [result, setResult] = useState<CalcResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    // Client-side: need total area OR both length and width.
    const haveArea = area !== '' && Number(area) > 0;
    const haveDims = length !== '' && width !== '';
    if (!haveArea && !haveDims) {
      setError('Enter the total area, or both length and width.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await CalcAPI.waterMitigation(
        {
          ...(haveDims
            ? { length_ft: Number(length), width_ft: Number(width) }
            : {}),
          ...(haveArea ? { area_sf: Number(area) } : {}),
          ceiling_height_ft: ceiling,
          water_class: waterClass,
          water_category: category,
          chambers,
          wall_insets: insets,
          ...(perimeter !== '' ? { affected_perimeter_lf: Number(perimeter) } : {}),
        },
      );
      setResult(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
      setResult(null);
    } finally {
      setLoading(false);
    }
  }

  return (
    <ToolPageShell>
      <Eyebrow>Drying — free calculator</Eyebrow>
      <h1 className="mt-2 font-ui text-4xl font-bold tracking-tight sm:text-[2.75rem] sm:leading-tight">
        Water Mitigation Equipment Calculator (IICRC S500)
      </h1>
      <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-muted-foreground">
        Size air movers and dehumidification for a structural dry-out using the
        IICRC S500 method. Enter the room, the water class, and the category,
        and the tool returns equipment counts with an S500-referenced
        justification you can put in the estimate and bill in Xactimate.
      </p>

      <div className="mt-8 grid gap-8 lg:grid-cols-2">
        <form
          onSubmit={onSubmit}
          className="space-y-4 rounded-lg border border-border bg-card p-5 sm:p-6"
          aria-label="Water mitigation inputs"
        >
          <div className="grid grid-cols-2 gap-3">
            <NumberField
              label="Length"
              suffix="ft"
              value={length}
              min={0}
              onChange={(v) => setLength(Number.isNaN(v) ? '' : v)}
            />
            <NumberField
              label="Width"
              suffix="ft"
              value={width}
              min={0}
              onChange={(v) => setWidth(Number.isNaN(v) ? '' : v)}
            />
          </div>
          <NumberField
            label="Total affected area"
            suffix="SF"
            value={area}
            min={0}
            onChange={(v) => setArea(Number.isNaN(v) ? '' : v)}
            hint="Optional. Enter this instead of length and width for an irregular room."
          />
          <NumberField
            label="Ceiling height"
            suffix="ft"
            value={ceiling}
            min={1}
            onChange={setCeiling}
          />
          <SelectField<WaterClass>
            label="Water class (evaporation load)"
            value={waterClass}
            onChange={(v) => setWaterClass(Number(v) as WaterClass)}
            options={[
              { value: 1, label: 'Class 1 - part of a room' },
              { value: 2, label: 'Class 2 - whole room, under 24 in wet height' },
              { value: 3, label: 'Class 3 - walls and ceiling saturated' },
              { value: 4, label: 'Class 4 - bound water in wood, plaster, concrete' },
            ]}
            hint="How much evaporation load: 1 part of a room, 2 whole room under 24 in wet height, 3 walls and ceiling saturated, 4 bound water in wood, plaster, or concrete."
          />
          <SelectField<WaterCategory>
            label="Water category (contamination)"
            value={category}
            onChange={(v) => setCategory(Number(v) as WaterCategory)}
            options={[
              { value: 1, label: 'Category 1 - clean supply line' },
              { value: 2, label: 'Category 2 - gray water (appliance or overflow)' },
              { value: 3, label: 'Category 3 - sewage or outside flood water' },
            ]}
            hint="Contamination: 1 clean supply line, 2 gray water (appliance or overflow), 3 sewage or outside flood water."
          />
          <div className="grid grid-cols-2 gap-3">
            <NumberField
              label="Drying chambers"
              value={chambers}
              min={1}
              step="1"
              onChange={setChambers}
              hint="Separate sealed drying areas. Usually 1 unless areas are tented off separately."
            />
            <NumberField
              label="Wall insets"
              value={insets}
              min={0}
              step="1"
              onChange={setInsets}
              hint="Corners and offsets that need their own air mover."
            />
          </div>
          <NumberField
            label="Affected perimeter"
            suffix="LF"
            value={perimeter}
            min={0}
            onChange={(v) => setPerimeter(Number.isNaN(v) ? '' : v)}
            hint="Optional. Wet wall length, used to check the air-mover count."
          />

          <Button type="submit" isLoading={loading} className="w-full">
            Size the equipment
          </Button>
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
        </form>

        <div>
          {result ? (
            <CalcResults result={result} />
          ) : (
            <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
              Enter the affected area and water class, then select Size the
              equipment to see air mover and dehumidifier counts with an S500
              justification.
            </div>
          )}
        </div>
      </div>

      <AppPromo
        className="mt-14"
        context="In the app, the drying setup writes itself from the flood intake — rooms, classes, and per-day WTR equipment lines land in the estimate automatically."
      />

      <MethodSection title="How the equipment is sized">
        <p>
          The tool follows the IICRC S500 drying method. Air movers are sized
          from the affected area and the water class, because the class
          describes how much of the room is wet and how fast it will give up
          water. As a working baseline the S500 references roughly one air mover
          for the first wet wall in a room plus one for each additional wall,
          and adds a unit for each offset, inset, or closet that would otherwise
          sit in dead air. Higher classes and wet ceilings raise the count
          because there is more surface evaporating at once.
        </p>
        <p>
          Dehumidification is sized from the volume of the wet space and the
          class, then divided by the pints-per-day rating of the units you plan
          to run. The category of water changes the scope rather than the
          airflow: category 2 and category 3 losses bring containment,
          antimicrobial application, and removal of porous materials that cannot
          be cleaned, and the tool flags those so they are not left out of the
          estimate. Every count is a starting point that a technician confirms
          against daily moisture readings, which is exactly how S500 expects
          drying to be documented.
        </p>
      </MethodSection>

      <section className="mt-14">
        <DimensionDivider label="Classes & categories" />
        <h2 className="mb-3 mt-5 font-ui text-2xl font-bold tracking-tight">
          Water classes and categories in plain language
        </h2>
        <div className="max-w-3xl space-y-3 text-[15px] leading-relaxed text-muted-foreground">
          <p>
            Two numbers drive a water loss, and they answer different questions.
            The class tells you how much drying equipment the room needs. The
            category tells you how much of the room has to come out. Get both
            right and the estimate holds up.
          </p>
          <p>
            <span className="font-semibold text-foreground">Class</span> is the
            evaporation load. Class 1 is a small wet area with low-permeance
            material and little to dry. Class 2 is a whole room with water wicked
            up the wall under 24 inches, the everyday supply-line or dishwasher
            loss. Class 3 is water that came from above, so the ceiling and the
            full height of the walls are saturated and the count of drying
            equipment climbs. Class 4 is bound water trapped in hardwood,
            plaster, lath, or concrete, which needs specialty low-humidity
            drying and the most time. When people search for water damage class
            2 drying equipment, this is the axis they mean: more class, more air
            movers and dehumidifiers.
          </p>
          <p>
            <span className="font-semibold text-foreground">Category</span> is
            contamination, and it decides scope. Category 1 is clean water from a
            broken supply line. Category 2 is gray water from an appliance
            discharge or a toilet overflow with no solids. Category 3 water is
            grossly contaminated: sewage backups, rising ground water, and
            wind-driven flood water from outside. A category 3 loss is not a dry
            in place job. The porous materials that soaked it up get removed, the
            cavity is cleaned and treated, and containment holds the line while
            the work happens, so the scope grows well past the drying setup.
            Time also matters here, because clean water left standing degrades to
            a higher category, and a category 1 loss that sat over a weekend is
            documented and scoped as category 2.
          </p>
        </div>
      </section>

      <FaqSection items={FAQ_ITEMS} />

      <SourcesSection
        sources={[
          {
            name: 'IICRC S500 Standard for Professional Water Damage Restoration',
            detail:
              'The industry standard method for classifying water losses and sizing drying equipment.',
          },
          {
            name: 'AHAM dehumidifier capacity ratings',
            detail:
              'Pints-per-day ratings used to size the dehumidifier into the correct Xactimate WTR band.',
          },
          {
            name: 'Xactimate WTR equipment items (priced at import)',
            detail:
              'Every equipment line resolves to a real Xactimate WTR item. This tool outputs the counts and codes; Xactimate prices them against your price list at import.',
          },
        ]}
      />
    </ToolPageShell>
  );
}
